import React, { useContext, useEffect, useState, useRef } from "react";
import { Modal, Button, Row, Col, ListGroup, Spinner, Alert } from "react-bootstrap";
import { IdentityContext } from "../../../../../../context/identity-context";
import ListingCalendarService from "../../../../../../services/listing-calendar-service";
import RejectModal from "./reservation-reject-modal";
import SuccessModal from "./reservation-success-modal";
import UnblockDateModal from "./unblock-date-modal";

/**
 * Props:
 * - show
 * - onHide
 * - reservationsList: array of occurrences for the selected date.
 *    each occurrence shape: { id, institution, email, notes, status, rejectReason, listingDateKey, listingDateOriginal, raw }
 * - onAccept(reservationId)
 * - onReject(reservationId, rejectReason)
 */
const ReservationRequestModal = ({ show, onHide, reservationsList = [], onAccept, onReject }) => {
  const { user } = useContext(IdentityContext);
  const listingCalendarService = new ListingCalendarService();

  const [localReservations, setLocalReservations] = useState(reservationsList);
  useEffect(() => setLocalReservations(reservationsList), [reservationsList]);

  const [loadingMap, setLoadingMap] = useState({});
  const [error, setError] = useState(null);

  // Reject modal state (id only)
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReservation, setRejectReservation] = useState(null);
  const [rejectError, setRejectError] = useState(null);

  // Unblock modal state (stores the full occurrence object)
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [unblockReservation, setUnblockReservation] = useState(null);
  const [unblockError, setUnblockError] = useState(null);

  // Success modal (used for approve flow)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const successTimeoutRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState("");

  // If true, closing the parent modal will reload the page.
  // This is set after a successful reject so the page refresh happens when user closes the modal.
  const [reloadOnClose, setReloadOnClose] = useState(false);

  const getAdminListingOfficerName = () => {
    return (
      user?.attributes?.["custom:display_name"] ||
      user?.username ||
      user?.attributes?.email ||
      "listing-officer"
    );
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  // When the modal is shown anew, reset reloadOnClose so we don't persist stale state.
  useEffect(() => {
    if (show === true) {
      setReloadOnClose(false);
      setError(null);
      setRejectError(null);
      setUnblockError(null);
    }
  }, [show]);

  const handleApprove = async (reservationId) => {
    setError(null);
    setLoadingMap((m) => ({ ...m, [reservationId]: true }));
    try {
      const adminListingOfficer = getAdminListingOfficerName();
      await listingCalendarService.adminLoApproveReservations(reservationId, adminListingOfficer);

      setLocalReservations((prev) => prev.map((r) => (r.id === reservationId ? { ...r, status: "approved" } : r)));

      if (onAccept) {
        try { onAccept(reservationId); } catch (cbErr) { /* swallow */ }
      }

      setSuccessMessage("Reservation approved successfully.");
      setShowSuccessModal(true);

      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => window.location.reload(), 5000);

    } catch (err) {
      const msg = err?.message || JSON.stringify(err) || "Unknown error";
      setError(`Failed to approve reservation: ${msg}`);
    } finally {
      setLoadingMap((m) => ({ ...m, [reservationId]: false }));
    }
  };

  // ---------- Reject flow ----------
  const handleRejectClick = (reservationId) => {
    setRejectError(null);
    setRejectReservation(reservationId);
    setShowRejectModal(true);
  };

  const handleConfirmReject = async (reason = "") => {
    if (!rejectReservation) return;
    const reservationId = rejectReservation;

    setRejectError(null);
    setLoadingMap((m) => ({ ...m, [reservationId]: true }));

    try {
      const adminListingOfficer = getAdminListingOfficerName();
      await listingCalendarService.adminLoRejectReservations(reservationId, adminListingOfficer, reason || "");

      // update local view: make it read-only by setting status to rejected and storing reject reason
      setLocalReservations((prev) => prev.map((r) => (r.id === reservationId ? { ...r, status: "rejected", rejectReason: reason } : r)));

      if (onReject) {
        try { onReject(reservationId, reason); } catch (cbErr) { /* swallow */ }
      }

      // mark the modal to reload on close.
      setReloadOnClose(true);

      // Close the inner RejectModal (keep the parent ReservationRequestModal visible)
      setShowRejectModal(false);
      setRejectReservation(null);

    } catch (err) {
      const msg = err?.message || JSON.stringify(err) || "Unknown error";
      setRejectError(`Failed to reject reservation: ${msg}`);
    } finally {
      setLoadingMap((m) => ({ ...m, [reservationId]: false }));
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectReservation(null);
    setRejectError(null);
  };

  // ---------- Unblock flow (include dateId in payload) ----------
  // Helper: convert "MM-DD-YYYY" to "YYYY-MM-DD"
  const mmddyyyyToIso = (mmddyyyy) => {
    if (!mmddyyyy) return null;
    const parts = mmddyyyy.split("-");
    if (parts.length !== 3) return null;
    const [mm, dd, yyyy] = parts;
    if (!mm || !dd || !yyyy) return null;
    return `${yyyy.padStart(4, "0")}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };

  // Try to find dateId in occ.raw.listingDates that corresponds to this occurrence's listing date
  const getDateIdFromOccurrence = (occ) => {
    if (!occ) return null;
    const raw = occ.raw;
    if (!raw || !Array.isArray(raw.listingDates)) return null;

    // Prefer matching against listingDateKey (YYYY-MM-DD)
    const targetIso = occ.listingDateKey || mmddyyyyToIso(occ.listingDateOriginal);

    for (const ld of raw.listingDates) {
      if (!ld) continue;
      // ld.date could be "MM-DD-YYYY" in your sample; convert to ISO form for comparison
      const ldIso = mmddyyyyToIso(ld.date) || ld.date;
      if (targetIso && ldIso === targetIso) {
        return ld.dateId || ld.dateId === 0 ? ld.dateId : null;
      }
      // also try direct match against listingDateOriginal (MM-DD-YYYY)
      if (occ.listingDateOriginal && ld.date === occ.listingDateOriginal) {
        return ld.dateId || ld.dateId === 0 ? ld.dateId : null;
      }
    }

    // If not found, return null so we still include a dateId field (null) in payload.
    return null;
  };

  const handleUnblockClick = (occurrence) => {
    // occurrence is the full occurrence object
    setUnblockError(null);
    setUnblockReservation(occurrence);
    setShowUnblockModal(true);
  };

  const handleConfirmUnblock = async (reason = "") => {
    if (!unblockReservation) return;
    const reservationId = unblockReservation.id;

    setUnblockError(null);
    setLoadingMap((m) => ({ ...m, [reservationId]: true }));

    try {
      const adminListingOfficer = getAdminListingOfficerName();

      // extract dateId for the specific listing date being unblocked
      const dateId = getDateIdFromOccurrence(unblockReservation);
      await listingCalendarService.adminLoUnblockDate(dateId, reservationId, adminListingOfficer, reason);

      // After unblocking, make it actionable again, set status to "pending" and clear rejectReason.
      setLocalReservations((prev) => prev.map((r) =>
        (r.id === reservationId ? { ...r, status: "pending", rejectReason: null } : r)
      ));

      window.location.reload();

    } catch (err) {
      const msg = err?.message || JSON.stringify(err) || "Unknown error";
      setUnblockError(`Failed to unblock reservation: ${msg}`);
    } finally {
      setLoadingMap((m) => ({ ...m, [reservationId]: false }));
    }
  };

  const handleCancelUnblock = () => {
    setShowUnblockModal(false);
    setUnblockReservation(null);
    setUnblockError(null);
  };

  const handleCloseSuccessModal = () => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    setShowSuccessModal(false);
    window.location.reload();
  };

  // actionable reservations: pending or accepted (approved/accepted)
  const actionableReservations = (localReservations || []).filter(r => {
    const s = (r.status || "").toLowerCase();
    return s === "pending" || s === "approved" || s === "accepted";
  });

  const isAcceptedStatus = (status = "") => {
    const s = (status || "").toLowerCase();
    return s === "approved" || s === "accepted";
  };

  // Centralized hide handler for the parent modal.
  // If reloadOnClose is true, reload the page. Otherwise call the provided onHide callback.
  const handleModalHide = () => {
    if (reloadOnClose) {
      // give the browser a brief moment to process UI changes if needed,
      // but reload immediately to show fresh state.
      window.location.reload();
    } else {
      if (typeof onHide === "function") onHide();
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleModalHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Reservation Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

          {/* Inform the user they should close to refresh after a reject */}
          {reloadOnClose && (
            <Alert variant="info">
              Reservation rejected. Close this dialog to refresh the page and see updates.
            </Alert>
          )}

          {(!localReservations || localReservations.length === 0) ? (
            <div>No reservations available.</div>
          ) : (
            <ListGroup variant="flush">
              {localReservations.map((occ, idx) => {
                const loading = !!loadingMap[occ.id];

                return (
                  <ListGroup.Item key={`${occ.id}-${idx}`}>
                    <Row>
                      <Col xs={4}><strong>Institution:</strong></Col>
                      <Col>{occ.institution || "-"}</Col>
                    </Row>
                    <Row>
                      <Col xs={4}><strong>Email Address:</strong></Col>
                      <Col>{occ.email || "-"}</Col>
                    </Row>
                    <Row>
                      <Col xs={4}><strong>Listing Date:</strong></Col>
                      <Col>{occ.listingDateKey || occ.listingDateOriginal || "-"}</Col>
                    </Row>
                    <Row>
                      <Col xs={4}><strong>Status:</strong></Col>
                      <Col>{occ.status || "pending"}</Col>
                    </Row>

                    {occ.notes && (
                      <Row>
                        <Col xs={4}><strong>Notes:</strong></Col>
                        <Col>{occ.notes}</Col>
                      </Row>
                    )}

                    {occ.rejectReason && (
                      <Row>
                        <Col xs={4}><strong>Reject Reason:</strong></Col>
                        <Col style={{ wordBreak: 'break-word' }}>{occ.rejectReason}</Col>
                      </Row>
                    )}

                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </Modal.Body>

        {/* Footer: action buttons grouped to the right, Close is the far-right button */}
        <Modal.Footer className="align-items-center">
          <div className="ms-auto d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            {actionableReservations.length === 0 && (
              <div className="text-muted small">No actions available</div>
            )}

            {actionableReservations.map((occ) => {
              const loading = !!loadingMap[occ.id];
              const label = `${occ.institution || "Reservation"}${occ.listingDateKey ? ` (${occ.listingDateKey})` : ""}`;

              // accepted/approved: show only Unblock
              if (isAcceptedStatus(occ.status)) {
                return (
                  <div key={`footer-accepted-${occ.id}`} className="d-flex align-items-center">

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleUnblockClick(occ)}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                          Unblock
                        </>
                      ) : (
                        "Unblock"
                      )}
                    </Button>
                  </div>
                );
              }

              // pending: show Accept + Reject
              return (
                <div key={`footer-pending-${occ.id}`} className="d-flex align-items-center">

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleApprove(occ.id)}
                    className="me-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                        Approving...
                      </>
                    ) : (
                      "Accept"
                    )}
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRejectClick(occ.id)}
                    disabled={loading}
                  >
                    Reject
                  </Button>
                </div>
              );
            })}

            <Button variant="secondary" onClick={handleModalHide} className="ms-2">Close</Button>
          </div>
        </Modal.Footer>
      </Modal>

      <RejectModal
        show={showRejectModal}
        onHide={handleCancelReject}
        onConfirm={handleConfirmReject}
        error={rejectError}
      />

      <UnblockDateModal
        show={showUnblockModal}
        onHide={handleCancelUnblock}
        onConfirm={handleConfirmUnblock}
        error={unblockError}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={handleCloseSuccessModal}
      />
    </>
  );
};

export default ReservationRequestModal;
